import React, { useState, useMemo, useEffect } from 'react';
import {
  Receipt, Search, X, Eye, CreditCard, CheckSquare, Square, ChevronDown, ChevronUp
} from 'lucide-react';

// 开票状态徽章
const InvoiceStatusBadge = ({ status }) => {
  const colorMap = {
    '待开票': 'bg-amber-100 text-amber-700',
    '审批中': 'bg-blue-100 text-blue-700',
    '已开票': 'bg-teal-100 text-teal-700',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// 付款状态徽章
const PaymentStatusBadge = ({ status }) => {
  const colorMap = {
    '未发起': 'bg-slate-100 text-slate-700',
    '已发起': 'bg-green-100 text-green-700',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// 批量付款弹窗组件
const BatchPaymentModal = ({ isOpen, onClose, onConfirm, invoices, onToggleSelect, selectedIds }) => {
  // 按供应商分组
  const groupedBySupplier = useMemo(() => {
    const groups = {};
    invoices.forEach(doc => {
      if (!groups[doc.supplierName]) {
        groups[doc.supplierName] = [];
      }
      groups[doc.supplierName].push(doc);
    });
    return groups;
  }, [invoices]);

  const selectedDocs = invoices.filter(doc => selectedIds.includes(doc.id));
  const totalAmount = selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-800">批量付款</h3>
            <p className="text-sm text-slate-500 mt-1">按供应商分组显示可发起付款的发票单</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedBySupplier).length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Receipt size={48} className="mx-auto text-slate-300 mb-4" />
              <p>暂无可发起付款的发票单</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedBySupplier).map(([supplier, docs]) => {
                const supplierSelectedIds = docs.filter(d => selectedIds.includes(d.id)).map(d => d.id);
                const isAllSelected = supplierSelectedIds.length === docs.length;
                const supplierTotal = docs.reduce((sum, doc) => sum + (doc.amount || 0), 0);

                return (
                  <div key={supplier} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* 供应商标题栏 */}
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            docs.forEach(doc => {
                              onToggleSelect(doc.id, !isAllSelected);
                            });
                          }}
                          className={`p-1 rounded ${isAllSelected ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                          {isAllSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                        </button>
                        <span className="font-medium text-slate-800">{supplier}</span>
                        <span className="text-sm text-slate-500">{docs.length}条发票单</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">总金额: </span>
                        <span className="font-medium text-teal-600">¥{supplierTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* 发票单列表 */}
                    <div className="divide-y divide-slate-100">
                      {docs.map(doc => {
                        const isSelected = selectedIds.includes(doc.id);
                        return (
                          <div
                            key={doc.id}
                            onClick={() => onToggleSelect(doc.id, !isSelected)}
                            className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleSelect(doc.id, !isSelected);
                                }}
                                className={`p-1 rounded ${isSelected ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                              >
                                {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                              </button>
                              <div>
                                <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
                                <span className="mx-2 text-slate-300">|</span>
                                <span className="text-sm text-slate-500">{doc.invoiceDate}</span>
                              </div>
                            </div>
                            <div className="text-sm">
                              <span className="font-medium text-teal-600">¥{doc.amount?.toLocaleString()}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部操作栏 */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              已选择 <span className="font-medium text-slate-800">{selectedDocs.length}</span> 条发票单，
              总金额 <span className="font-medium text-teal-600">¥{totalAmount.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={() => {
                  onConfirm(selectedDocs);
                  onClose();
                }}
                disabled={selectedDocs.length === 0}
                className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                确认发起付款
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InvoiceManageView = ({ deliveries = [], orders = [], onDetailClick, onGenerateDocument, onSettlementClick, initialFilter, setInitialFilter }) => {
  // 筛选状态 - 初始化时如果有 initialFilter 则使用，否则为空
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState(() => initialFilter?.status || '');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState(() => initialFilter?.paymentStatus || '');

  // 批量付款状态
  const [showBatchPaymentModal, setShowBatchPaymentModal] = useState(false);
  const [batchSelectedIds, setBatchSelectedIds] = useState([]);

  // 展开的结算单行
  const [expandedSettlementRows, setExpandedSettlementRows] = useState([]);

  // 切换结算单展开状态
  const toggleSettlementExpand = (docId) => {
    setExpandedSettlementRows(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // 处理从首页传递的初始筛选条件（仅在 initialFilter 变化时更新）
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.status !== undefined) {
        setFilterInvoiceStatus(initialFilter.status);
      }
      if (initialFilter.paymentStatus !== undefined) {
        setFilterPaymentStatus(initialFilter.paymentStatus);
      }
      // 清除 initialFilter 表示已处理
      setInitialFilter?.(null);
    }
  }, [initialFilter, setInitialFilter]);

  // 从 deliveries 提取发票单数据（按发票单号聚合，合并单只显示一行）
  const invoices = useMemo(() => {
    const groups = {};

    deliveries.forEach(delivery => {
      if (delivery.invoice) {
        const invoiceNo = delivery.invoice.no;

        if (!groups[invoiceNo]) {
          // 判断发票状态
          let invoiceStatus = '已开票';
          if (delivery.payment) {
            invoiceStatus = '已开票';
          } else if (delivery.invoice.pendingApproval) {
            invoiceStatus = '审批中';
          }

          // 判断付款状态
          const paymentStatus = delivery.payment ? '已发起' : '未发起';

          groups[invoiceNo] = {
            id: invoiceNo,
            docNo: invoiceNo,
            docType: 'invoice',
            orderIds: [],
            orderNos: [],
            deliveryIds: [],
            deliveryNos: [],
            settlementNos: [],
            supplierId: delivery.supplierId,
            supplierName: delivery.supplierName,
            baseId: delivery.baseId,
            baseName: delivery.baseName,
            invoiceType: delivery.invoice.type || '增值税专用发票',
            amount: 0,
            invoiceDate: delivery.invoice.at,
            invoiceFile: delivery.invoice.file,
            status: invoiceStatus,
            paymentStatus: paymentStatus,
            createdAt: delivery.invoice.at,
            deliveries: [],
          };
        }

        // 累加数据
        const group = groups[invoiceNo];
        if (!group.orderIds.includes(delivery.orderId)) {
          group.orderIds.push(delivery.orderId);
          group.orderNos.push(delivery.orderNo);
        }
        group.deliveryIds.push(delivery.id);
        group.deliveryNos.push(delivery.deliveryNo);
        group.deliveries.push(delivery);

        // 获取结算单号
        const settlementNo = delivery.settlement?.settlementNo;
        if (settlementNo && !group.settlementNos.includes(settlementNo)) {
          group.settlementNos.push(settlementNo);
        }

        // 累加金额
        group.amount += delivery.invoice.amount || 0;

        // 更新开票日期（取最早的）
        const invoiceDate = delivery.invoice.at;
        if (invoiceDate && invoiceDate < group.invoiceDate) {
          group.invoiceDate = invoiceDate;
          group.createdAt = invoiceDate;
        }
      }
    });

    // 转换为数组并标记是否为合并单
    return Object.values(groups).map(group => ({
      ...group,
      amount: Math.round(group.amount * 100) / 100,
      isMerged: group.settlementNos.length >= 2,
    }));
  }, [deliveries]);

  // 获取唯一的供应商列表
  const suppliers = useMemo(() => {
    const supplierSet = new Set();
    invoices.forEach(doc => {
      if (doc.supplierName) supplierSet.add(doc.supplierName);
    });
    return Array.from(supplierSet).sort();
  }, [invoices]);

  // 筛选后的数据
  const filteredDocs = useMemo(() => {
    let result = [...invoices];

    // 搜索筛选
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(doc =>
        doc.docNo?.toLowerCase().includes(query) ||
        doc.supplierName?.toLowerCase().includes(query)
      );
    }

    // 供应商筛选
    if (filterSupplier) {
      result = result.filter(doc => doc.supplierName === filterSupplier);
    }

    // 开票状态筛选
    if (filterInvoiceStatus) {
      result = result.filter(doc => doc.status === filterInvoiceStatus);
    }

    // 付款状态筛选
    if (filterPaymentStatus) {
      result = result.filter(doc => doc.paymentStatus === filterPaymentStatus);
    }

    // 按时间倒序
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return result;
  }, [invoices, searchQuery, filterSupplier, filterInvoiceStatus, filterPaymentStatus]);

  // 获取可发起付款的发票单（已开票且未发起付款）
  const payableInvoices = useMemo(() => {
    return invoices.filter(doc => doc.status === '已开票' && doc.paymentStatus === '未发起');
  }, [invoices]);

  // 统计数据
  const stats = useMemo(() => {
    const total = invoices.length;
    const pendingApproval = invoices.filter(d => d.status === '审批中').length;
    const completed = invoices.filter(d => d.status === '已开票').length;
    const paymentInitiated = invoices.filter(d => d.paymentStatus === '已发起').length;
    const paymentNotInitiated = invoices.filter(d => d.paymentStatus === '未发起').length;
    const totalAmount = invoices.reduce((sum, d) => sum + (d.amount || 0), 0);
    return { total, pendingApproval, completed, paymentInitiated, paymentNotInitiated, totalAmount };
  }, [invoices]);

  // 批量付款 - 切换选择
  const toggleBatchSelect = (docId, isSelected) => {
    setBatchSelectedIds(prev =>
      isSelected
        ? [...prev, docId]
        : prev.filter(id => id !== docId)
    );
  };

  // 打开批量付款弹窗
  const openBatchPayment = () => {
    setBatchSelectedIds(payableInvoices.map(doc => doc.id));
    setShowBatchPaymentModal(true);
  };

  // 确认批量发起付款
  const confirmBatchPayment = (selectedDocs) => {
    onGenerateDocument?.({
      sourceType: 'invoice',
      targetType: 'payment',
      targetLabel: '付款单',
      sourceDocs: selectedDocs,
      totalAmount: selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0),
      suppliers: [...new Set(selectedDocs.map(d => d.supplierName))],
      isBatch: true,
    });
  };

  // 清除所有筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterSupplier('');
    setFilterInvoiceStatus('');
    setFilterPaymentStatus('');
  };

  const hasActiveFilters = searchQuery || filterSupplier || filterInvoiceStatus || filterPaymentStatus;

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">发票单管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有发票单据</p>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">全部发票</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
              <Receipt size={24} className="text-teal-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">审批中</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.pendingApproval}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Receipt size={24} className="text-blue-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">已开票</p>
              <p className="text-2xl font-bold text-teal-600 mt-1">{stats.completed}</p>
            </div>
            <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
              <Receipt size={24} className="text-teal-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">未发起付款</p>
              <p className="text-2xl font-bold text-slate-600 mt-1">{stats.paymentNotInitiated}</p>
            </div>
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
              <CreditCard size={24} className="text-slate-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">发票总金额</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">¥{stats.totalAmount.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
              <Receipt size={24} className="text-orange-500" />
            </div>
          </div>
        </div>
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索发票号、供应商..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 供应商筛选 */}
            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部供应商</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>

            {/* 开票状态筛选 */}
            <select
              value={filterInvoiceStatus}
              onChange={(e) => setFilterInvoiceStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">开票状态</option>
              <option value="审批中">审批中</option>
              <option value="已开票">已开票</option>
            </select>

            {/* 付款状态筛选 */}
            <select
              value={filterPaymentStatus}
              onChange={(e) => setFilterPaymentStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">付款状态</option>
              <option value="未发起">未发起</option>
              <option value="已发起">已发起</option>
            </select>

            {/* 批量付款按钮 */}
            {payableInvoices.length > 0 && (
              <button
                onClick={openBatchPayment}
                className="flex items-center gap-1 px-4 py-2.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors"
              >
                <CreditCard size={16} />
                批量付款
                <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                  {payableInvoices.length}
                </span>
              </button>
            )}

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 数据表格 */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">发票号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">供应商</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">关联结算单</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">金额</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">开票日期</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">开票状态</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">付款状态</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <Receipt size={40} className="text-slate-300" />
                      <p>暂无发票单数据</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredDocs.map((doc) => {
                  const isExpanded = expandedSettlementRows.includes(doc.id);
                  const settlementCount = doc.settlementNos?.length || 0;

                  return (
                  <React.Fragment key={doc.id}>
                    <tr
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="p-3">
                        <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
                        {doc.isMerged && (
                          <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] rounded">合并</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-slate-800 font-medium">{doc.supplierName}</span>
                      </td>
                      <td className="p-3">
                        {settlementCount === 0 ? (
                          <span className="text-xs text-slate-400">-</span>
                        ) : settlementCount === 1 ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSettlementClick?.(doc.settlementNos[0]);
                            }}
                            className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            {doc.settlementNos[0]}
                          </button>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onSettlementClick?.(doc.settlementNos[0]);
                              }}
                              className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              {doc.settlementNos[0]}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSettlementExpand(doc.id);
                              }}
                              className="flex items-center gap-0.5 text-xs text-slate-400 hover:text-slate-600"
                            >
                              <span>+{settlementCount - 1}</span>
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-sm font-bold text-teal-600">¥{doc.amount?.toLocaleString()}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-slate-600">{doc.invoiceDate}</span>
                      </td>
                      <td className="p-3 text-center"><InvoiceStatusBadge status={doc.status} /></td>
                      <td className="p-3 text-center"><PaymentStatusBadge status={doc.paymentStatus} /></td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => onDetailClick?.(doc)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                        >
                          <Eye size={12} />
                          详情
                        </button>
                      </td>
                    </tr>
                    {/* 展开显示多个结算单 */}
                    {settlementCount > 1 && isExpanded && (
                      <tr className="bg-slate-50/50">
                        <td colSpan="8" className="p-0">
                          <div className="px-12 py-2 flex flex-wrap gap-2">
                            {doc.settlementNos.map((no, idx) => (
                              <button
                                key={no}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSettlementClick?.(no);
                                }}
                                className="inline-flex items-center px-2 py-1 bg-white border border-slate-200 rounded text-xs font-mono text-blue-600 hover:text-blue-700 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                              >
                                {idx === 0 && <span className="text-green-500 mr-1">●</span>}
                                {no}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 底部统计 */}
        {filteredDocs.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredDocs.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{invoices.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* 批量付款弹窗 */}
      <BatchPaymentModal
        isOpen={showBatchPaymentModal}
        onClose={() => setShowBatchPaymentModal(false)}
        onConfirm={confirmBatchPayment}
        invoices={payableInvoices}
        onToggleSelect={toggleBatchSelect}
        selectedIds={batchSelectedIds}
      />
    </div>
  );
};

export default InvoiceManageView;
